// Default payment plans, keyed by CashflowPlanKey.
//
// Tuned for typical paid-conference cash patterns. The trough position is
// most sensitive to the venue plan (big deposit, big balance) and the
// ticket sales curve (when the bulk of revenue actually lands), so those
// two are the highest-leverage values to revisit if real-world feedback
// suggests the defaults are off.

import type { CashflowPlanKey } from './types';

export type PaymentMilestone = {
  pct: number; // share of the line item's total, 0..1
  daysBeforeEvent: number; // positive = before event, negative = after
};
export type PaymentPlan = PaymentMilestone[];

export const DEFAULT_PLANS: Record<CashflowPlanKey, PaymentPlan> = {
  // Conference costs
  venue:      [{ pct: 0.5,  daysBeforeEvent: 180 }, { pct: 0.5,  daysBeforeEvent: 30 }],
  catering:   [{ pct: 0.2,  daysBeforeEvent: 90  }, { pct: 0.8,  daysBeforeEvent: 7  }],
  security:   [{ pct: 1.0,  daysBeforeEvent: 14  }],
  av:         [{ pct: 0.3,  daysBeforeEvent: 60  }, { pct: 0.7,  daysBeforeEvent: 7  }],
  marketing:  [{ pct: 0.33, daysBeforeEvent: 120 }, { pct: 0.33, daysBeforeEvent: 60 }, { pct: 0.34, daysBeforeEvent: 14 }],
  additional: [{ pct: 1.0,  daysBeforeEvent: 30  }],
  // Meetup costs — paid close to the event with no big deposits.
  refreshments: [{ pct: 1.0, daysBeforeEvent: 3   }],
  food:         [{ pct: 1.0, daysBeforeEvent: 3   }],
  // Swag often needs lead time for production / printing.
  swags:        [{ pct: 1.0, daysBeforeEvent: 45  }],
  materials:    [{ pct: 1.0, daysBeforeEvent: 14  }],
  // Speakers are typically reimbursed shortly after the event (negative
  // value = days AFTER event).
  speakers:     [{ pct: 1.0, daysBeforeEvent: -7  }],
  // Revenue
  // Sponsors typically invoice ~T-90d on Net 30 → cash lands ~T-60d.
  sponsors:   [{ pct: 1.0,  daysBeforeEvent: 60  }],
  addons:     [{ pct: 1.0,  daysBeforeEvent: 30  }],
  // Three-bucket ticket sales curve: slow Early Bird, mid-cycle warmup,
  // late spike.
  tickets:    [{ pct: 0.2,  daysBeforeEvent: 180 }, { pct: 0.3, daysBeforeEvent: 60 }, { pct: 0.5, daysBeforeEvent: 14 }],
};

export const PLAN_LABELS: Record<CashflowPlanKey, string> = {
  tickets:      'Tickets',
  sponsors:     'Sponsors',
  addons:       'Add-ons',
  venue:        'Venue',
  catering:     'Catering',
  security:     'Security',
  av:           'A/V',
  marketing:    'Marketing',
  additional:   'Additional',
  refreshments: 'Refreshments',
  food:         'Food',
  swags:        'Swag',
  materials:    'Materials',
  speakers:     'Speakers',
};

export const CONFERENCE_REVENUE_PLAN_KEYS: CashflowPlanKey[] = ['tickets', 'sponsors', 'addons'];
export const MEETUP_REVENUE_PLAN_KEYS: CashflowPlanKey[] = ['tickets', 'sponsors'];

export const CONFERENCE_COST_PLAN_KEYS: CashflowPlanKey[] = [
  'venue', 'catering', 'security', 'av', 'marketing', 'additional',
];
export const MEETUP_COST_PLAN_KEYS: CashflowPlanKey[] = [
  'venue', 'refreshments', 'food', 'swags', 'materials', 'speakers',
];

// Back-compat — defaults to conference layout for any old caller.
export const REVENUE_PLAN_KEYS = CONFERENCE_REVENUE_PLAN_KEYS;
export const COST_PLAN_KEYS = CONFERENCE_COST_PLAN_KEYS;
